import { inject, Injectable } from '@angular/core';
import { ActiveCartService, CartActions } from '@cart/data-access';
import { Service } from '@checkout-v2/const';
import { CheckoutActions, CheckoutApiService } from '@checkout-v2/data-access';
import { Actions, createEffect, EffectNotification, ofType, OnRunEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  pairwise,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { ServicesService, TimeSlotsService } from '../services';

@Injectable()
export class TimeSlotsEffects implements OnRunEffects {
  private readonly actions$ = inject(Actions);
  private readonly checkoutApiService = inject(CheckoutApiService);
  private readonly activeCartService = inject(ActiveCartService);
  private readonly servicesService = inject(ServicesService);
  private readonly timeSlotsService = inject(TimeSlotsService);

  // Загрузить список таймслотов
  // Отправить запрос на бекенд
  loadTimeSlots$: Observable<CheckoutActions.LoadTimeSlotsSuccess | CheckoutActions.LoadTimeSlotsFail> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CheckoutActions.LoadTimeSlots>(CheckoutActions.LOAD_TIME_SLOTS),
        map((action) => action.payload),
        concatLatestFrom(() => this.activeCartService.getActiveCartId()),
        filter(([, cartId]) => !!cartId),
        switchMap(([payload, cartId]) =>
          this.checkoutApiService.loadTimeSlots(cartId, payload).pipe(
            map((data) => new CheckoutActions.LoadTimeSlotsSuccess(data)),
            catchError(() => of(new CheckoutActions.LoadTimeSlotsFail())),
          ),
        ),
      ),
  );

  // Изменилась выбранная услуга (выбрана и валидна)
  // Загрузить список таймслотов
  loadTimeSlotsOnSelectService$: Observable<CheckoutActions.LoadTimeSlots> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.LoadSavedDeliveryAddressListSuccess>(CheckoutActions.LOAD_SERVICES_SUCCESS),
      switchMap(() =>
        this.servicesService.getServicesActiveOption().pipe(
          startWith(undefined),
          pairwise(),
          filter((data): data is [Service | undefined, Service] => {
            const [prevService, currentService] = data;

            return (
              !!currentService &&
              !currentService.reason &&
              JSON.stringify(currentService) !== JSON.stringify(prevService)
            );
          }),
          // перед запросом таймслотов необходимо дождаться кастомерконсаймента и корзинки
          switchMap(([, currentService]) =>
            this.actions$.pipe(
              ofType(CheckoutActions.UPDATE_CUSTOMER_CONSIGNMENT_SUCCESS),
              take(1),
              switchMap(() =>
                this.actions$.pipe(ofType<CartActions.LoadCartSuccess>(CartActions.LOAD_CART_SUCCESS), take(1)),
              ),
              map(() => currentService),
            ),
          ),
          concatLatestFrom(() => this.servicesService.isValid()),
          filter(([, isServicesValid]) => isServicesValid),
          map(
            ([currentService]) =>
              new CheckoutActions.LoadTimeSlots({
                transportIds: currentService.transportIds,
              }),
          ),
        ),
      ),
    ),
  );

  // Изменилась выбранная услуга (не выбрана, выбрана но с reason или не валидна)
  // Сбросить список таймслотов
  resetTimeSlotsOnSelectService$: Observable<CheckoutActions.ResetLoadedTimeSlots> = createEffect(() =>
    combineLatest({
      selectedServiceCode: this.servicesService.getServicesActiveOptionCode(),
      isValid: this.servicesService.isValid(),
    }).pipe(
      distinctUntilChanged(
        (prev, current) => prev.selectedServiceCode === current.selectedServiceCode && prev.isValid === current.isValid,
      ),
      filter(({ selectedServiceCode, isValid }) => !selectedServiceCode || !isValid),
      concatLatestFrom(() => this.timeSlotsService.getTimeSlotsState()),
      filter(([, state]) => !!state.error || !!state.success),
      map(() => new CheckoutActions.ResetLoadedTimeSlots()),
    ),
  );

  // --------------------------------------------

  // Загрузить список таймслотов (для кастомной даты)
  // Отправить запрос на бекенд
  loadTimeSlotsForCustomDate$: Observable<
    CheckoutActions.LoadTimeSlotsForCustomDateSuccess | CheckoutActions.LoadTimeSlotsForCustomDateFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.LoadTimeSlotsForCustomDate>(CheckoutActions.LOAD_TIME_SLOTS_FOR_CUSTOM_DATE),
      map((action) => action.payload),
      concatLatestFrom(() => this.activeCartService.getActiveCartId()),
      filter(([, cartId]) => !!cartId),
      switchMap(([payload, cartId]) =>
        this.checkoutApiService.loadTimeSlots(cartId, payload).pipe(
          map((data) => new CheckoutActions.LoadTimeSlotsForCustomDateSuccess(data)),
          catchError(() => of(new CheckoutActions.LoadTimeSlotsForCustomDateFail())),
        ),
      ),
    ),
  );

  // Выбрана кастомная дата
  // Загрузить список таймслотов (для кастомной даты)
  loadTimeSlotsAfterSelectCustomDate$: Observable<CheckoutActions.LoadTimeSlotsForCustomDate> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.SelectTimeSlotsCustomDate>(CheckoutActions.SELECT_TIME_SLOTS_CUSTOM_DATE),
      map((action) => action.payload),
      concatLatestFrom(() => this.servicesService.getServicesActiveOption()),
      filter((data): data is [string, Service] => {
        const [, selectedService] = data;

        return !!selectedService;
      }),
      map(
        ([date, selectedService]) =>
          new CheckoutActions.LoadTimeSlotsForCustomDate({
            dateFrom: date,
            dateTo: date,
            transportIds: selectedService.transportIds,
          }),
      ),
    ),
  );

  // Загрузить список таймслотов (обычных и для кастомной даты)
  // Сброс загруженных таймслотов (для кастомной даты)
  resetTimeSlotsForCustomDateOnLoadRegularTimeSlots$: Observable<CheckoutActions.ResetLoadedTimeSlotsForCustomDate> =
    createEffect(() =>
      this.actions$.pipe(
        ofType<CheckoutActions.LoadTimeSlots>(
          CheckoutActions.LOAD_TIME_SLOTS,
          CheckoutActions.LOAD_TIME_SLOTS_FOR_CUSTOM_DATE,
        ),
        map((action) => action.payload),
        concatLatestFrom(() => this.timeSlotsService.getTimeSlotsForCustomDateState()),
        filter(([, state]) => !!state.error || !!state.success),
        map(() => new CheckoutActions.ResetLoadedTimeSlotsForCustomDate()),
      ),
    );

  // --------------------------------------------

  // Загружен список таймслотов
  // Сбросить выбранный таймслот, если выбранного таймслота нет в списке
  resetSelectedTimeSlotOnLoad$: Observable<CheckoutActions.ResetTimeSlotsActiveOption> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.LoadTimeSlotsSuccess>(CheckoutActions.LOAD_TIME_SLOTS_SUCCESS),
      concatLatestFrom(() => [this.timeSlotsService.getTimeSlots(), this.timeSlotsService.getSelectedTimeSlotId()]),
      filter(([, loadedTimeSlots = [], selectedTimeSlotId]) => {
        if (!selectedTimeSlotId) {
          return false;
        }

        // Проверить, есть ли выбранный слот в списке загруженных
        const isSelectedSlotExists = loadedTimeSlots.some((timeSlot) =>
          timeSlot.groups.some((group) => group.options.some((option) => option.id === selectedTimeSlotId)),
        );

        return !isSelectedSlotExists;
      }),
      map(() => new CheckoutActions.ResetTimeSlotsActiveOption()),
    ),
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return this.actions$.pipe(
      ofType(CheckoutActions.CHECKOUT_INIT),
      exhaustMap(() => resolvedEffects$.pipe(takeUntil(this.actions$.pipe(ofType(CheckoutActions.CHECKOUT_DESTROY))))),
    );
  }
}
